import React, { FC, useEffect, useMemo, useState } from "react";
import { Notification, Select, Text } from "@telia/teddy";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { FORM_ID } from "../../../../../../constants";
import { Form, RootState } from "../../../../../../models";
import { CustomerExistingSubscription } from "../../../../../../models/state/retailer";
import { getActiveSubscription } from "../../../../checkout/state/selectors/cartSelectors";
import { getCustomerExistingAccounts } from "../../../state/selectors/customerSelectors";
import { getAccountId } from "../../../../checkout/state/selectors/accountSelectors";
import { getSubscriptions } from "../../../../checkout/state/selectors/subscriptionsSelectors";
import {
    isEmailTakenForTeliaTrygg as isEmailTakenForTeliaTryggSelector,
    isEmailTakenForTeliaSky as isEmailTakenForTeliaSkySelector,
} from "../../../state/selectors/customerInfoSelectors";
import { FieldSet, FormRow, FormRowFormats, Input } from "../../../../forms";
import { RapidForm, actionTypes as RF_AT } from "../../../../rapidform";
import { allValidationRules, VALIDATION_FIELD } from "../../../../rapidform/formValidation";
import { setValue as setPersonalDetailsValueAction } from "../../../state/actions";
import * as CUSTOMER_INFO_SELECTORS from "../../../state/selectors/customerInfoSelectors";
import { getFormById } from "../../../state/selectors/formSelectors";
import TEXT from "../../../text-consts";
import "../CustomerInfoForm/CustomerInfoForm.less";
import { capitalizeEachWord } from "../../../utils";

const LEGAL_OWNER_VALUE = "__legal_owner__";
const SOMEONE_ELSE_VALUE = "__noen_andre__";

interface TryggUserOption {
    value: string;
    label: string;
    firstName?: string;
    lastName?: string;
    birthDate?: string;
    email?: string;
}

interface Props {
    onSubmitCustomerDetails: () => void;
}

const TryggUserSelect: FC<Props> = ({ onSubmitCustomerDetails }) => {
    const dispatch = useDispatch();
    const [selectedUser, setSelectedUser] = useState("");

    const form: Form = useSelector((state: RootState) => getFormById(state, FORM_ID.CUSTOMER_PRODUCT_USER));
    const activeSubscriptionId = useSelector(getActiveSubscription);

    const selectedBan = useSelector(getAccountId);
    const existingSubscriptions: CustomerExistingSubscription[] = useSelector((state: RootState) => {
        const accounts = getCustomerExistingAccounts(state);
        if (selectedBan) {
            const current = accounts.find((a) => a.accountId === selectedBan);
            return current?.subscriptions || [];
        }
        return accounts.flatMap((account) => account.subscriptions || []);
    }, shallowEqual);
    const checkoutSubscriptions = useSelector(getSubscriptions);

    const isEmailTakenForTeliaTrygg = useSelector(isEmailTakenForTeliaTryggSelector);
    const isEmailTakenForTeliaSky = useSelector(isEmailTakenForTeliaSkySelector);
    const isNotFound = useSelector(CUSTOMER_INFO_SELECTORS.isNotFound);
    const isZipNeeded = useSelector(CUSTOMER_INFO_SELECTORS.isZipNeeded);

    const streetAddress = form.values?.user?.[VALIDATION_FIELD.STREET];
    const zipCode = form.values?.user?.[VALIDATION_FIELD.POSTCODE];

    const isSomeoneElse = selectedUser === SOMEONE_ELSE_VALUE;

    const options: TryggUserOption[] = useMemo(() => {
        const result: TryggUserOption[] = [];
        const seenNames = new Set<string>();

        // Product users from cart
        for (const [subId, sub] of Object.entries(checkoutSubscriptions)) {
            if (subId === activeSubscriptionId) continue;
            const pu = sub.productUser;
            if (!pu?.firstName || !pu.birthDate) continue;
            const fullName = `${pu.firstName} ${pu.lastName || ""}`.trim();
            const key = fullName.toLowerCase();
            if (seenNames.has(key)) continue;
            seenNames.add(key);

            result.push({
                value: `checkout-${subId}`,
                label: fullName,
                firstName: pu.firstName,
                lastName: pu.lastName,
                birthDate: pu.birthDate,
                email: pu.email,
            });
        }

        // Product users from existing subscriptions
        for (const sub of existingSubscriptions) {
            if (!sub.firstName) continue;
            const fullName = `${sub.firstName} ${sub.lastName || ""}`.trim();
            const key = fullName.toLowerCase();
            if (seenNames.has(key)) continue;
            seenNames.add(key);
            const formattedBirthDate = sub.birthDate?.split("-").reverse().join(".") || "";

            result.push({
                value: `existing-${sub.phoneNumber}`,
                label: capitalizeEachWord(fullName),
                firstName: sub.firstName,
                lastName: sub.lastName,
                birthDate: formattedBirthDate,
                email: sub.email,
            });
        }

        result.push({ value: SOMEONE_ELSE_VALUE, label: "Noen andre" });
        return result;
    }, [existingSubscriptions, checkoutSubscriptions, activeSubscriptionId]);

    const setFormField = (field: string, value: string) => {
        dispatch({
            type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.CUSTOMER_PRODUCT_USER),
            payload: {
                errorMessage: undefined,
                form: FORM_ID.CUSTOMER_PRODUCT_USER,
                inputName: `user-${field}`,
                inputValue: value,
                visible: true,
            },
        });
    };

    const handleUserChange = (value: string) => {
        setSelectedUser(value);

        const selected = options.find((o) => o.value === value);
        const isLegalOwner = value === LEGAL_OWNER_VALUE;

        dispatch(setPersonalDetailsValueAction("isProductUserDifferentThanLegalOwner", !isLegalOwner));

        if (value === SOMEONE_ELSE_VALUE) {
            setFormField(VALIDATION_FIELD.FIRST_NAME, "");
            setFormField(VALIDATION_FIELD.LAST_NAME, "");
            setFormField(VALIDATION_FIELD.BIRTHDATE, "");
            setFormField(VALIDATION_FIELD.PRODUCT_USER_EMAIL, "");
            return value;
        }

        if (selected) {
            setFormField(VALIDATION_FIELD.FIRST_NAME, selected.firstName || "");
            setFormField(VALIDATION_FIELD.LAST_NAME, selected.lastName || "");
            setFormField(VALIDATION_FIELD.BIRTHDATE, selected.birthDate || "");
            setFormField(VALIDATION_FIELD.PRODUCT_USER_EMAIL, selected.email || "");
        }

        return value;
    };

    useEffect(() => {
        if (!selectedUser && options.length > 1) {
            handleUserChange(options[0].value);
        }
    }, [options, selectedUser]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally omit handleUserChange to avoid effect churn

    const handleSubmit = () => {
        const email = form.values?.user?.[VALIDATION_FIELD.PRODUCT_USER_EMAIL];
        if (!email?.trim()) {
            dispatch({
                type: RF_AT.FORM_VALIDATE_ALL(FORM_ID.CUSTOMER_PRODUCT_USER),
                payload: {
                    errors: {
                        user: {
                            [VALIDATION_FIELD.PRODUCT_USER_EMAIL]: "Dette feltet er obligatorisk.",
                        },
                    },
                },
            });
            return;
        }
        const emailRule = allValidationRules[VALIDATION_FIELD.PRODUCT_USER_EMAIL];
        if (emailRule?.regex && !emailRule.regex.test(email.trim())) {
            dispatch({
                type: RF_AT.FORM_VALIDATE_ALL(FORM_ID.CUSTOMER_PRODUCT_USER),
                payload: {
                    errors: {
                        ...form.errors,
                        user: {
                            ...form.errors?.user,
                            [VALIDATION_FIELD.PRODUCT_USER_EMAIL]: emailRule.errorMessage,
                        },
                    },
                },
            });
            return;
        }
        onSubmitCustomerDetails();
    };

    return (
        <div className="customer-form">
            <RapidForm
                id={FORM_ID.CUSTOMER_PRODUCT_USER}
                action={"#"}
                errors={form.errors}
                values={form.values}
                visible={form.visible}
                started={form.started}
                onSubmit={handleSubmit}
            >
                <FieldSet name="user">
                    <Text as="p" variant="paragraph-100-bold" mb="100">
                        Hvem skal bruke Telia Trygg?
                    </Text>
                    <Select
                        name="trygg-user"
                        options={options.map((o) => o.value)}
                        value={selectedUser}
                        onValueChange={handleUserChange}
                        data-di-mask
                    >
                        <Select.Trigger>
                            <Select.Value placeholder="Velg" />
                        </Select.Trigger>
                        <Select.Content style={{ zIndex: 10 }}>
                            <Select.Viewport>
                                {options.map((option) => (
                                    <Select.Item key={option.value} value={option.value}>
                                        {option.label}
                                    </Select.Item>
                                ))}
                            </Select.Viewport>
                        </Select.Content>
                    </Select>

                    {isSomeoneElse && (
                        <>
                            <Input
                                type="text"
                                name={VALIDATION_FIELD.FIRST_NAME}
                                label={TEXT.PERSONAL_DETAILS.FIRSTNAME}
                                data-di-mask
                            />
                            <Input
                                type="text"
                                name={VALIDATION_FIELD.LAST_NAME}
                                label={TEXT.PERSONAL_DETAILS.LASTNAME}
                                data-di-mask
                            />
                            <FormRow format={FormRowFormats.FORTY_SIXTY} data-di-mask>
                                <Input
                                    type="tel"
                                    name={VALIDATION_FIELD.BIRTHDATE}
                                    label={TEXT.PERSONAL_DETAILS.BIRTHDATE}
                                    placeholder="DD.MM.ÅÅÅÅ"
                                    data-di-mask
                                />
                                <div></div>
                            </FormRow>
                            {isZipNeeded && streetAddress && (
                                <Input
                                    type="text"
                                    name={VALIDATION_FIELD.STREET}
                                    label={TEXT.PERSONAL_DETAILS.STREET}
                                    data-di-mask
                                    disabled
                                />
                            )}
                            {isZipNeeded && (
                                <>
                                    {!isNotFound && (
                                        <Notification type="tile" variant="information" mt="200">
                                            <Notification.Icon />
                                            <Notification.Text>
                                                Vi klarte ikke hente ut informasjon i folkeregisteret basert på
                                                fødselsdato og navn, og trenger derfor postnummer for å kunne
                                                identifisere riktig person. Postnummeret må samsvare med hva som er
                                                registrert i Folkeregisteret. Vennligst se over og prøv igjen.
                                            </Notification.Text>
                                        </Notification>
                                    )}
                                    {isNotFound && (
                                        <Notification type="tile" variant="warning" mt="200">
                                            <Notification.Icon />
                                            <Notification.Text>
                                                Vi klarte ikke hente ut informasjon i folkeregisteret basert på
                                                fødselsdato, navn og postnummer. Vennligst sjekk at opplysningene er
                                                korrekte og samsvarer med hva som er registrert i Folkeregisteret, og
                                                prøv igjen.
                                            </Notification.Text>
                                        </Notification>
                                    )}
                                    <FormRow format={FormRowFormats.FORTY_SIXTY} data-di-mask>
                                        <Input
                                            type="text"
                                            name={VALIDATION_FIELD.POSTCODE}
                                            label={TEXT.PERSONAL_DETAILS.POSTCODE}
                                        />
                                        <Input
                                            disabled={!!zipCode}
                                            type="text"
                                            name={VALIDATION_FIELD.CITY}
                                            label={TEXT.PERSONAL_DETAILS.CITY}
                                        />
                                    </FormRow>
                                </>
                            )}
                        </>
                    )}

                    <Input
                        type="email"
                        name={VALIDATION_FIELD.PRODUCT_USER_EMAIL}
                        label={TEXT.PERSONAL_DETAILS.EMAIL}
                        placeholder="ola@nordmann.no"
                        required
                        helpText={TEXT.PERSONAL_DETAILS.EMAIL_HELP_TEXT_TELIA_TRYGG}
                        data-di-mask
                    />
                    {(isEmailTakenForTeliaTrygg || isEmailTakenForTeliaSky) && (
                        <Notification type="tile" variant="warning" className="subscription-age-not-ok">
                            <Notification.Icon />
                            <Notification.Text>
                                {TEXT.ADD_MEMBER.TELIA_X_SERVICE.UNIQUE_EMAIL(
                                    isEmailTakenForTeliaTrygg,
                                    isEmailTakenForTeliaSky,
                                    true,
                                )}
                            </Notification.Text>
                        </Notification>
                    )}
                </FieldSet>
            </RapidForm>
        </div>
    );
};

export default TryggUserSelect;
